/**
 * G05: WKT Parser
 * Parse ULDK WKT responses to coordinate arrays
 */

import type { PL2000Coordinate } from './types'

/**
 * Parse ULDK WKT POLYGON response to coordinate array
 * Input: "SRID=2180;POLYGON((637285.55 478123.45, 637290.12 478125.67, ...))"
 * Output: PL2000Coordinate[]
 */
export function parseWktPolygon(wkt: string): PL2000Coordinate[] {
  // Remove SRID prefix if present
  let cleaned = wkt
  if (cleaned.startsWith('SRID=')) {
    const semicolonIdx = cleaned.indexOf(';')
    if (semicolonIdx !== -1) {
      cleaned = cleaned.substring(semicolonIdx + 1)
    }
  }

  // Extract coordinates from POLYGON((x y, x y, ...))
  const match = cleaned.match(/POLYGON\s*\(\s*\(\s*(.+?)\s*\)\s*\)/i)
  if (!match) {
    throw new Error(`Invalid WKT POLYGON format: ${wkt.substring(0, 100)}...`)
  }

  const coordString = match[1]
  const coordPairs = coordString.split(',')

  const coordinates: PL2000Coordinate[] = []
  for (const pair of coordPairs) {
    const trimmed = pair.trim()
    const [xStr, yStr] = trimmed.split(/\s+/)

    if (!xStr || !yStr) {
      throw new Error(`Invalid coordinate pair: ${pair}`)
    }

    const x = parseFloat(xStr)
    const y = parseFloat(yStr)

    if (isNaN(x) || isNaN(y)) {
      throw new Error(`Could not parse coordinates: ${pair}`)
    }

    coordinates.push({ x, y })
  }

  // Remove closing point if it duplicates the first (polygon convention)
  if (coordinates.length > 1) {
    const first = coordinates[0]
    const last = coordinates[coordinates.length - 1]
    if (first.x === last.x && first.y === last.y) {
      coordinates.pop()
    }
  }

  return coordinates
}

/**
 * Calculate polygon area using Shoelace formula
 * Returns area in square meters (for PL-2000 coordinates)
 */
export function calculatePolygonArea(coords: PL2000Coordinate[]): number {
  if (coords.length < 3) {
    return 0
  }

  let area = 0
  const n = coords.length

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    area += coords[i].x * coords[j].y
    area -= coords[j].x * coords[i].y
  }

  return Math.abs(area) / 2
}

/**
 * Calculate polygon centroid
 */
export function calculateCentroid(coords: PL2000Coordinate[]): PL2000Coordinate {
  if (coords.length === 0) {
    throw new Error('Cannot calculate centroid of empty polygon')
  }

  if (coords.length === 1) {
    return { ...coords[0] }
  }

  if (coords.length === 2) {
    return {
      x: (coords[0].x + coords[1].x) / 2,
      y: (coords[0].y + coords[1].y) / 2
    }
  }

  // For polygon, use the centroid formula
  let cx = 0
  let cy = 0
  let signedArea = 0
  const n = coords.length

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    const x0 = coords[i].x
    const y0 = coords[i].y
    const x1 = coords[j].x
    const y1 = coords[j].y

    const a = x0 * y1 - x1 * y0
    signedArea += a
    cx += (x0 + x1) * a
    cy += (y0 + y1) * a
  }

  signedArea *= 0.5
  cx /= (6 * signedArea)
  cy /= (6 * signedArea)

  return { x: cx, y: cy }
}

/**
 * Point-in-polygon test using ray casting algorithm
 */
export function isPointInPolygon(px: number, py: number, polygon: PL2000Coordinate[]): boolean {
  let inside = false
  const n = polygon.length
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y
    const xj = polygon[j].x, yj = polygon[j].y
    if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
      inside = !inside
    }
  }
  return inside
}

/**
 * Parse ULDK pipe-delimited response
 * Format: "status\ngeom_wkt|id|voivodeship|county|commune|parcel"
 */
export interface ULDKParsedResponse {
  status: number
  geomWkt: string
  id: string
  voivodeship: string
  county: string
  commune: string
  parcel: string
}

export function parseULDKResponse(response: string): ULDKParsedResponse | null {
  const lines = response.trim().split('\n')

  if (lines.length < 2) {
    return null
  }

  const status = parseInt(lines[0], 10)

  // Status: -1 = not found, 0 = ok, >0 = error
  if (status !== 0) {
    return null
  }

  const parts = lines[1].split('|')
  if (parts.length < 6) {
    return null
  }

  return {
    status,
    geomWkt: parts[0],
    id: parts[1],
    voivodeship: parts[2],
    county: parts[3],
    commune: parts[4],
    parcel: parts[5]
  }
}
